import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './MobileDashboard.css';
import { MOBILE_DEFAULT_THEME } from '../utils/appearanceDefaults';
import AppIcon from '../components/AppIcon';

const isStandalone = () =>
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;

const MOBILE_THEMES = [['original', 'Classic'], ['liquido', 'Standard']];
const readAppearance = key => {
    try {
        const saved = JSON.parse(localStorage.getItem(key) || '{}');
        if (saved.theme === 'claro') { saved.theme = 'liquido'; localStorage.setItem(key,JSON.stringify(saved)); }
        return {
            key,
            theme: MOBILE_THEMES.some(([theme]) => theme === saved.theme) ? saved.theme : MOBILE_DEFAULT_THEME,
            view: saved.view === 'icons' ? 'icons' : 'list',
        };
    } catch { return { key, theme: MOBILE_DEFAULT_THEME, view: 'list' }; }
};

const MobileDashboard = () => {
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    const isAdmin = user?.role === 'admin';
    const appearanceKey = 'mobile_appearance_' + (user?.id || user?.username || 'guest');
    const [appearance, setAppearance] = useState(() => readAppearance(appearanceKey));
    const [showAppearance, setShowAppearance] = useState(false);
    const savedAppearance = appearance.key === appearanceKey ? appearance : readAppearance(appearanceKey);
    const theme = savedAppearance.theme;
    const view = isAdmin ? savedAppearance.view : 'list';
    const changeAppearance = change => {
        if (change.view && !isAdmin) return;
        const next = {
            ...savedAppearance,
            ...change,
            ...(change.theme ? { themeExplicit: true } : {}),
            key: appearanceKey
        };
        setAppearance(next);
        try { localStorage.setItem(appearanceKey, JSON.stringify(next)); } catch { /* Apenas nesta sessão. */ }
    };

    useEffect(() => {
        // Show install banner only in browser (not already installed as PWA)
        const dismissed = sessionStorage.getItem('pwa-banner-dismissed');
        if (!isStandalone() && !dismissed) {
            setShowInstallBanner(true);
        }
    }, []);

    const handleDismissBanner = () => {
        sessionStorage.setItem('pwa-banner-dismissed', '1');
        setShowInstallBanner(false);
    };

    const handleLogout = () => {
        if (window.confirm('Deseja sair da aplicação?')) {
            logout();
            navigate('/login');
        }
    };

    return (
        <div className="mobile-dashboard" data-mobile-theme={theme} data-mobile-view={view}>
            {showInstallBanner && (
                <div className="pwa-install-banner">
                    <span className="pwa-banner-icon"><AppIcon name="upload" size={23} /></span>
                    <div className="pwa-banner-text">
                        <strong>Sem barra do Safari</strong>
                        <span>Partilhar → "Adicionar ao Ecrã de Início"</span>
                    </div>
                    <button className="pwa-banner-close" aria-label="Fechar aviso de instalação" onClick={handleDismissBanner}><AppIcon name="close" size={17} /></button>
                </div>
            )}

            <header className="dashboard-header">
                <div className="header-content">
                    <img src="/assets/logo-black.png" alt="Sprod" className="dashboard-logo" />
                    <div className="user-info">
                        <h1>Olá, {user?.nome_completo?.split(' ')[0] || user?.username || 'Utilizador'}</h1>
                    </div>
                </div>
            </header>

            <div className="mobile-appearance">
                    <button type="button" className="mobile-appearance-toggle"
                        aria-expanded={showAppearance} aria-controls="mobile-appearance-options"
                        onClick={() => setShowAppearance(open => !open)}>
                        <AppIcon name="appearance" size={18} /> Aparência <AppIcon name={showAppearance ? 'up' : 'down'} size={16} />
                    </button>
                    {showAppearance && (
                        <section id="mobile-appearance-options" className="mobile-appearance-options" aria-label="Aparência do menu">
                            <span className="mobile-appearance-label">Tema</span>
                            <div className="mobile-appearance-choices">
                                {MOBILE_THEMES.map(([value, label]) => (
                                    <button type="button" key={value} aria-pressed={theme === value}
                                        onClick={() => changeAppearance({ theme: value })}>{label}</button>
                                ))}
                            </div>
                            {isAdmin && (
                                <>
                                    <span className="mobile-appearance-label">Vista do menu</span>
                                    <div className="mobile-appearance-choices">
                                        <button type="button" aria-pressed={view === 'list'} onClick={() => changeAppearance({ view: 'list' })}><AppIcon name="list" size={17} /> Lista</button>
                                        <button type="button" aria-pressed={view === 'icons'} onClick={() => changeAppearance({ view: 'icons' })}><AppIcon name="dashboard" size={17} /> Ícones</button>
                                    </div>
                                </>
                            )}
                        </section>
                    )}
            </div>

            <h2 className="section-title">Menu Principal</h2>

            <div className="dashboard-grid">
                <button className="mobile-dashboard-card primary" onClick={() => navigate('/despesas')}>
                    <span className="icon"><AppIcon name="document" size={23} /></span>
                    <span className="label">Despesas</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/veiculos')}>
                    <span className="icon"><AppIcon name="vehicle" size={23} /></span>
                    <span className="label">Registo Kms</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/registo-horas')}>
                    <span className="icon"><AppIcon name="clock" size={23} /></span>
                    <span className="label">Registo Horas</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/registo-incidente')}>
                    <span className="icon"><AppIcon name="warning" size={23} /></span>
                    <span className="label">Incidentes / Comunicações</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/ficha-levantamento')}>
                    <span className="icon"><AppIcon name="forms" size={23} /></span>
                    <span className="label">Fichas Levantamento</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/ficha-manutencao')}>
                    <span className="icon"><AppIcon name="maintenance" size={23} /></span>
                    <span className="label">Manutenção</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/equipamentos-mobile')}>
                    <span className="icon"><AppIcon name="equipment" size={23} /></span>
                    <span className="label">Equipamentos</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/andaime-verificacao')}>
                    <span className="icon"><AppIcon name="andaime" size={23} /></span>
                    <span className="label">Verif. Andaimes</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/andaimes-historico')}>
                    <span className="icon"><AppIcon name="forms" size={23} /></span>
                    <span className="label">Hist. Andaimes</span>
                </button>

                {/* Substitui as antigas "Fichas Técnicas": FDS e fichas técnicas por produto */}
                <button className="mobile-dashboard-card primary" onClick={() => navigate('/produtos-quimicos')}>
                    <span className="icon"><AppIcon name="test" size={23} /></span>
                    <span className="label">Produtos químicos (FDS)</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/ficha-procedimentos-seguranca')}>
                    <span className="icon"><AppIcon name="shield" size={23} /></span>
                    <span className="label">F. Proc. Segurança</span>
                </button>

                <button className="mobile-dashboard-card primary" onClick={() => navigate('/contactos-uteis')}>
                    <span className="icon"><AppIcon name="phone" size={23} /></span>
                    <span className="label">Contactos Úteis</span>
                </button>

                {/* Guias rápidos (PDF e imagens) — no fim, para não mudar o menu dos próprios manuais */}
                <button className="mobile-dashboard-card primary" onClick={() => navigate('/manuais')}>
                    <span className="icon"><AppIcon name="document" size={23} /></span>
                    <span className="label">Manuais</span>
                </button>

                {/* Avaliação de riscos, documentos internos e instruções — também no fim, pela mesma razão */}
                <button className="mobile-dashboard-card primary" onClick={() => navigate('/documentos-mobile')}>
                    <span className="icon"><AppIcon name="forms" size={23} /></span>
                    <span className="label">Documentos</span>
                </button>
            </div>

            <footer className="dashboard-footer">
                <button className="logout-btn-large" onClick={handleLogout}>
                    <AppIcon name="exit" size={19} /> Sair da Conta
                </button>
            </footer>
        </div>
    );
};

export default MobileDashboard;
